import { fetchEventSource } from '@microsoft/fetch-event-source';
import type { IntlShape } from 'react-intl';

import AuthenticationStore from '~/auth/authenticationStore';
import { refreshAccessToken } from '~/auth/refreshAccessToken';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { LOGIN_CONSTANTS } from '~/features/login/constants';
import { camelizeKeys } from '~/redux/api/transformers/utils';

const authStore = AuthenticationStore();

interface sseCallbacks<
  Event = unknown,
  EventError = unknown,
  EventResponse = Response,
> {
  /**
   * useSse will thry refreshing access token if it fails this callback gets invoked.
   * @param EventError - reason for error.
   * @returns void
   */
  onAuthenticationError: (err: EventError) => void;
  /**
   * Pass callback to handle SSE connection close.
   * @returns void
   */
  onClose: () => void;
  /**
   * Pass callback to handle SSE retryable errors.
   * @param EventError - reason for error.
   * @returns void
   */
  onRateLimitError: () => void;
  /**
   * Pass callback to handle SSE errors that close a connection.
   * @param EventError - reason for error.
   * @returns void
   */
  onError: (err: EventError) => void;
  /**
   * Pass callback to handle SSE message events.
   * @param Event - is unknown until type is specified by the consumer.
   * @returns void
   */
  onMessage: (event: Event) => void;
  /**
   * Pass callback to handle SSE opening a connection.
   * Place where you can handle logic based on response statuses.
   * @param Response - fetch response object.
   * @returns void
   */
  onOpen: (response: EventResponse) => void;
  /**
   * Pass callback to handle SSE stream timeout.
   * Called when no messages are received within the specified timeout period.
   * @returns void
   */
  onTimeout?: () => void;
}

export interface SseRequestProps {
  //* Request method.
  method: 'POST' | 'GET';
  //* Request headers.
  headers?: {
    'Content-Type': string;
  };
  //* `fetch-event-source` uses the browsers Page Visibility API so the connection closes if document is hidden.(Like when you switch tabs)
  //* By default this is true but we can pass false to disable this.
  //* https://github.com/Azure/fetch-event-source#fetch-event-source
  openWhenHidden?: boolean;
  //* Timeout in milliseconds for stream inactivity. If no messages are received within this time, onTimeout will be called.
  timeout?: number;
}

export class RateLimitError extends Error {}
export class AuthenticationError extends Error {}

export const useSse = (intl: IntlShape) => {
  const { toast } = useToast();

  const SYSTEM_ERROR = intl.formatMessage({
    id: 'useSSe.systemError',
    defaultMessage:
      'Apologies, the system is currently experiencing technical difficulties and cannot process your request at this time.',
    description: 'System technical difficulties error message',
  });

  const RETRY_LATER_ERROR = intl.formatMessage({
    id: 'useSSe.retryLaterError',
    defaultMessage: 'Something went wrong. Please try again later.',
    description: 'Try again later error message',
  });

  const REFRESH_AND_RETRY_ERROR = intl.formatMessage({
    id: 'useSSe.refreshAndRetryError',
    defaultMessage: 'An error occurred. Refresh the page and try again.',
    description: 'Refresh page and retry error message',
  });

  const createSseConnection = (
    url: string,
    callbacks: sseCallbacks,
    requestProps: SseRequestProps = { method: 'GET', openWhenHidden: false },
    hasRetried = false,
  ) =>
    //* Adding a catch here for authentication errors. This is so we retry until we get updated refresh token.
    //* Github issue: https://github.com/Azure/fetch-event-source/issues/33
    setupConnection(url, callbacks, requestProps).catch(async (err) => {
      if (hasRetried) {
        toast({
          message: err?.detail ?? REFRESH_AND_RETRY_ERROR,
          status: 'danger',
        });
        return callbacks.onAuthenticationError?.(err);
      } else if (err instanceof AuthenticationError) {
        await refreshAccessToken();
        createSseConnection(url, callbacks, requestProps, true);
      }
    });

  const setupConnection = (
    url: string,
    callbacks: sseCallbacks,
    requestProps: SseRequestProps,
  ) => {
    let timeoutId: NodeJS.Timeout | undefined;
    const abortController = new AbortController();

    const clearTimeoutTimer = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const resetTimeoutTimer = () => {
      clearTimeoutTimer();

      if (requestProps.timeout && callbacks.onTimeout) {
        timeoutId = setTimeout(() => {
          toast({ message: SYSTEM_ERROR, status: 'danger' });
          callbacks.onTimeout?.();
          // Abort the connection to force closure
          abortController.abort();
        }, requestProps.timeout);
      }
    };

    //* This has to be async because onopen return `Promise<void>.`
    const handleOpen = async (res: Response) => {
      switch (res.status) {
        case 200:
          callbacks.onOpen?.(res);
          // Start the timeout timer when connection opens
          resetTimeoutTimer();
          break;
        case 429:
          toast({ message: RETRY_LATER_ERROR, status: 'danger' });
          callbacks.onRateLimitError?.();
          throw new RateLimitError();
        case 401:
          throw new AuthenticationError();
        default:
          throw new Error();
      }
    };

    return fetchEventSource(url, {
      credentials: 'include',
      headers: {
        ...(requestProps.headers || { Accept: 'text/event-stream' }),
        ...(authStore.getCsrf()
          ? { [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: authStore.getCsrf() }
          : {}),
        ...(authStore.getAccessToken()
          ? {
              Authorization: `Bearer ${authStore.getAccessToken()}`,
            }
          : {}),
      },
      method: requestProps.method,
      signal: abortController.signal,
      openWhenHidden: requestProps.openWhenHidden,
      onclose: () => {
        clearTimeoutTimer();
        callbacks.onClose?.();
      },
      onerror: (err) => {
        clearTimeoutTimer();
        if (
          (err instanceof DOMException && err.name === 'AbortError') || // Timeout already handled by timeout callback, don't show duplicate error
          err instanceof AuthenticationError ||
          err instanceof RateLimitError
        ) {
          throw err;
        } else {
          toast({ message: err?.detail ?? SYSTEM_ERROR, status: 'danger' });
          //* Calls callback here to catch errors that may happen mid connection.
          callbacks.onError?.(err);
          throw err; // rethrow to stop the connection else it will auto retry.
        }
      },
      onmessage: (event) => {
        /*
         * FE needs to ignore empty events.
         * Reason: SSE has a ping event in order to make sure the connection stays open and the server is still alive (in case there's a big gap between events).
         */
        if (event.data === '') {
          // Reset timeout even for ping events (empty events)
          resetTimeoutTimer();
          return;
        }

        // Reset timeout on each message
        resetTimeoutTimer();

        try {
          const eventData = JSON.parse(event.data);
          const parsedEventData = camelizeKeys(eventData);
          callbacks.onMessage?.(parsedEventData);
        } catch {
          console.error('Failed to parse server response.');
          throw new Error();
        }
      },
      onopen: handleOpen,
    });
  };

  return {
    createSseConnection,
  };
};
